import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  TextField,
  InputAdornment,
} from '@mui/material';
import { keyframes } from '@mui/system';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BoltIcon from '@mui/icons-material/Bolt';
import GroupIcon from '@mui/icons-material/Group';
import DescriptionIcon from '@mui/icons-material/Description';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import CategoryIcon from '@mui/icons-material/Category';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import ImageIcon from '@mui/icons-material/Image';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ForwardIcon from '@mui/icons-material/Forward';
import SearchIcon from '@mui/icons-material/Search';
import { useApp } from '../../context/AppContext';
import { USER_GROUP } from '../../models/types';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/dateFormat';
import { getApiUrl } from '../../utils/apiUrl';
import { SELECTED_APP_ID_KEY, SELECTED_APP_STAFF_KEY, SELECTED_APP_NAME_KEY, SELECTED_APP_USER_HAS_ACCESS_KEY, STAFF_DEPARTMENT_IDS_KEY } from '../shared/SelectAppPage';
import { isTicketUrgent } from '../../utils/ticketUrgency';

const shakeKeyframes = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
`;

const ROW_FIELD_FLASH_MS = 2000;

const ALERT_SOUND_KEY = 'serviceProviderAlertSound';

const SOUND_PRESETS = [
  { id: 'beep', label: 'บี๊บสองครั้ง' },
  { id: 'chime', label: 'เสียงกระดิ่ง' },
  { id: 'soft', label: 'เสียงนุ่ม' },
  { id: 'single', label: 'เสียงสั้น' },
  { id: 'ding', label: 'ดิ้ง' },
];

function getAlertSoundId() {
  try {
    const saved = localStorage.getItem(ALERT_SOUND_KEY);
    if (SOUND_PRESETS.some(p => p.id === saved)) return saved;
  } catch (_) {}
  return 'beep';
}

function playNewReportSound(soundId) {
  const id = soundId || getAlertSoundId();
  try {
    const C = window.AudioContext || window.webkitAudioContext;
    if (!C) return;
    const ctx = new C();
    const play = (freq, start, duration, type = 'sine', vol = 0.12) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = type;
      gain.gain.setValueAtTime(vol, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
      osc.start(start);
      osc.stop(start + duration);
    };
    if (id === 'chime') {
      [523.25, 659.25, 783.99].forEach((f, i) => play(f, i * 0.15, 0.2));
    } else if (id === 'soft') {
      play(622, 0, 0.25, 'sine', 0.08);
      play(784, 0.22, 0.2, 'sine', 0.06);
    } else if (id === 'single') {
      play(880, 0, 0.15, 'sine', 0.1);
    } else if (id === 'ding') {
      play(1320, 0, 0.12, 'sine', 0.1);
      play(1760, 0.14, 0.15, 'sine', 0.08);
    } else {
      play(880, 0, 0.12);
      play(1108, 0.18, 0.12);
    }
  } catch (_) {}
}

const ServiceProviderDashboard = () => {
  const { currentUser, acceptHelpDesk, completeHelpDesk, checkSLAViolation } = useApp();
  const navigate = useNavigate();
  const [openAcceptConfirm, setOpenAcceptConfirm] = useState(false);
  const [itemToAcceptId, setItemToAcceptId] = useState(null);
  const [openCompleteConfirm, setOpenCompleteConfirm] = useState(false);
  const [itemToCompleteId, setItemToCompleteId] = useState(null);
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);
  const [newReportIds, setNewReportIds] = useState([]);
  const [alertSoundId, setAlertSoundId] = useState(() => getAlertSoundId());
  const acknowledgedNewIdsRef = useRef(new Set());
  const [ticketList, setTicketList] = useState([]);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [staffDepartments, setStaffDepartments] = useState([]);
  const [authVerifyUser, setAuthVerifyUser] = useState(null);
  const [authVerifyLoading, setAuthVerifyLoading] = useState(true);
  const [displayAppName, setDisplayAppName] = useState(null);
  const [, setAppDetail] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [priorityUpdatingId, setPriorityUpdatingId] = useState(null);
  const [updateSnackbar, setUpdateSnackbar] = useState({ open: false, severity: 'info', message: '' });
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [statusConfirmItem, setStatusConfirmItem] = useState(null);
  const [statusConfirmNewStatus, setStatusConfirmNewStatus] = useState('');
  const [statusConfirmDescription, setStatusConfirmDescription] = useState('');
  const [statusConfirmDepartment, setStatusConfirmDepartment] = useState('');
  const [allDepartments, setAllDepartments] = useState([]);
  const [rowFieldFlashById, setRowFieldFlashById] = useState({});
  const rowFieldFlashTimersRef = useRef({});
  const [filterSearch, setFilterSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const showCategoryField = currentUser?.group === USER_GROUP.STAFF && currentUser?.department === 'cooperative';

  const ticketsBase = useMemo(() => getApiUrl('/api/tickets/').replace(/\/$/, ''), []);
  const PRIORITY_OPTIONS = useMemo(() => [
    { value: 'normal', label: 'ไม่เร่งด่วน' },
    { value: 'high', label: 'เร่งด่วน' },
  ], []);
  const getPrioritySelectValue = (item) => (isTicketUrgent(item) ? 'high' : 'normal');

  const STATUS_OPTIONS = useMemo(() => [
    { value: 'pending', label: 'รอรับเรื่อง' },
    { value: 'forward', label: 'ส่งต่อ' },
    { value: 'in_progress', label: 'กำลังดำเนินการ' },
    { value: 'completed', label: 'เสร็จสิ้น' },
    { value: 'cancelled', label: 'ยกเลิก' },
  ], []);

  const FILTER_TYPE_OPTIONS = useMemo(() => [
    { value: '', label: 'ทั้งหมด' },
    { value: 'water', label: 'ด้านประปา' },
    { value: 'electricity', label: 'ด้านไฟฟ้า' },
    { value: 'cooperative', label: 'ด้าน อื่นๆ' },
  ], []);

  const FILTER_STATUS_OPTIONS = useMemo(() => [
    { value: '', label: 'สถานะ' },
    ...STATUS_OPTIONS,
  ], [STATUS_OPTIONS]);

  const selectedAppId = typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_APP_ID_KEY) : null;
  const staffAppIds = authVerifyUser?.staff_app_ids ?? currentUser?.staff_app_ids ?? [];
  const staffAppIdList = Array.isArray(staffAppIds) ? staffAppIds : [];
  const canShowManageButton = selectedAppId != null && staffAppIdList.some((appId) => Number(appId) === Number(selectedAppId));

  // โหลดแอปจาก GET /api/clients/apps/{id}/ (ชื่อ + staff สำหรับปุ่มจัดการสิทธิ์)
  useEffect(() => {
    const selectedAppId = localStorage.getItem(SELECTED_APP_ID_KEY);
    const fromStorage = localStorage.getItem(SELECTED_APP_NAME_KEY);
    if (!selectedAppId) {
      setDisplayAppName(null);
      setAppDetail(null);
      return;
    }
    if (fromStorage) {
      setDisplayAppName(fromStorage);
    }
    const appsBase = getApiUrl('/api/clients/apps/').replace(/\/$/, '');
    if (!appsBase) {
      setDisplayAppName(null);
      setAppDetail(null);
      return;
    }
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Token ${token}`, accept: 'application/json' } : { accept: 'application/json' };
    axios
      .get(`${appsBase}/${selectedAppId}/`, { headers })
      .then(({ data }) => {
        setAppDetail(data);
        const name = data?.name || '';
        setDisplayAppName(name);
        if (name) localStorage.setItem(SELECTED_APP_NAME_KEY, name);
      })
      .catch(() => {
        setDisplayAppName(null);
        setAppDetail(null);
      });
  }, []);

  // GET /api/user/auth/verify/ — เตรียมข้อมูล user สำหรับแสดงชื่อ
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthVerifyLoading(false);
      return;
    }
    setAuthVerifyLoading(true);
    const url = getApiUrl('/api/user/auth/verify/');
    axios.get(url, { headers: { Authorization: `Token ${token}` } })
      .then(({ data }) => setAuthVerifyUser(data))
      .catch(() => setAuthVerifyUser(null))
      .finally(() => setAuthVerifyLoading(false));
  }, []);

  // โหลด departments จาก API แล้วกรองด้วย staff_department_ids ใน localStorage
  useEffect(() => {
    const selectedAppId = localStorage.getItem(SELECTED_APP_ID_KEY);
    const appsBase = getApiUrl('/api/clients/apps/').replace(/\/$/, '');
    if (!selectedAppId || !appsBase) {
      setStaffDepartments([]);
      return;
    }
    let staffIds = [];
    try {
      const raw = localStorage.getItem(STAFF_DEPARTMENT_IDS_KEY);
      if (raw) staffIds = JSON.parse(raw);
      if (!Array.isArray(staffIds)) staffIds = [];
    } catch (_) {}
    const url = `${appsBase}/${selectedAppId}/departments/`;
    axios
      .get(url)
      .then(({ data }) => {
        const results = data?.results ?? (Array.isArray(data) ? data : []);
        const allowed = results.filter((d) => d != null && staffIds.includes(d.id));
        setStaffDepartments(allowed);
      })
      .catch(() => setStaffDepartments([]));
  }, []);

  // โหลด departments ทั้งหมดของแอป (สำหรับเลือกแผนกเมื่อส่งต่อ)
  useEffect(() => {
    const selectedAppId = localStorage.getItem(SELECTED_APP_ID_KEY);
    const appsBase = getApiUrl('/api/clients/apps/').replace(/\/$/, '');
    if (!selectedAppId || !appsBase) {
      setAllDepartments([]);
      return;
    }
    axios
      .get(`${appsBase}/${selectedAppId}/departments/`, {
        headers: { accept: 'application/json', ...(localStorage.getItem('token') ? { Authorization: `Token ${localStorage.getItem('token')}` } : {}) },
      })
      .then(({ data }) => {
        const results = data?.results ?? (Array.isArray(data) ? data : []);
        setAllDepartments(results.filter((d) => d != null));
      })
      .catch(() => setAllDepartments([]));
  }, []);

  const normalizeStatus = (raw) => {
    const s = String(raw || '').trim().toLowerCase();
    if (!s) return 'pending';
    if (s === 'pending') return 'pending';
    if (s === 'forward') return 'forward';
    if (s === 'in_progress' || s === 'in progress' || s === 'inprogress' || s === 'processing' || s === 'accepted') return 'in_progress';
    if (s === 'completed' || s === 'complete' || s === 'done' || s === 'resolved' || s === 'finished') return 'completed';
    if (s === 'cancelled' || s === 'canceled' || s === 'cancel' || s === 'void') return 'cancelled';
    return 'pending';
  };

  const mapApiTicket = (r) => ({
    id: r.id,
    title: r.title,
    description: r.description,
    department: r.department,
    department_name: r.department_name,
    category: /ประปา|น้ำ/i.test(r.department_name || '') ? 'water'
      : /ไฟฟ้า/i.test(r.department_name || '') ? 'electricity'
      : 'cooperative',
    location: r.location,
    status: normalizeStatus(r.status),
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    owner: r.owner,
    owner_username: r.owner_username,
    owner_fullname: r.owner_fullname,
    urgency: r.urgency,
    priority: r.priority,
  });

  const getDepartmentLabel = (department) => {
    const labels = { water: 'ด้านประปา', electricity: 'ด้านไฟฟ้า', cooperative: 'ด้าน อื่นๆ' };
    return labels[department] || 'ไม่ทราบสิทธิ์';
  };

  const getDepartmentChipStyle = (department) => {
    if (department === 'water') return { bgcolor: 'rgba(30, 144, 255, 0.10)', color: '#1e90ff', borderColor: 'rgba(30, 144, 255, 0.25)' };
    if (department === 'electricity') return { bgcolor: 'rgba(255, 152, 0, 0.10)', color: '#ff9800', borderColor: 'rgba(255, 152, 0, 0.25)' };
    if (department === 'cooperative') return { bgcolor: 'rgba(132, 94, 194, 0.10)', color: '#845EC2', borderColor: 'rgba(132, 94, 194, 0.25)' };
    return { bgcolor: 'rgba(0, 0, 0, 0.06)', color: 'rgba(0, 0, 0, 0.65)', borderColor: 'rgba(0, 0, 0, 0.12)' };
  };

  const departmentNameToCategory = (name) => {
    if (!name) return 'cooperative';
    if (/ประปา|น้ำ/i.test(name)) return 'water';
    if (/ไฟฟ้า/i.test(name)) return 'electricity';
    return 'cooperative';
  };

  const staffDisplay = (() => {
    if (!staffDepartments || staffDepartments.length === 0) {
      return { label: getDepartmentLabel(currentUser?.department), department: currentUser?.department };
    }
    const departments = staffDepartments.map((d) => departmentNameToCategory(d?.name));
    const labels = [...new Set(departments.map((d) => getDepartmentLabel(d)))];
    const label = labels.join(', ');
    const department = departments[0] ?? currentUser?.department;
    return { label: label || 'ไม่ทราบสิทธิ์', department };
  })();

  const relevantHelpDeskList = useMemo(() => {
    let list = ticketList || [];
    const q = (filterSearch || '').trim().toLowerCase();
    if (q) {
      list = list.filter((item) => {
        const title = (item.title || '').toLowerCase();
        const desc = (item.description || '').toLowerCase();
        return title.includes(q) || desc.includes(q);
      });
    }
    if (filterType) {
      list = list.filter((item) => (item.category || 'cooperative') === filterType);
    }
    if (filterStatus) {
      list = list.filter((item) => (item.status || '') === filterStatus);
    }
    return list;
  }, [ticketList, filterSearch, filterType, filterStatus]);

  useEffect(() => {
    setTablePage(0);
  }, [filterSearch, filterType, filterStatus]);

  const fetchTickets = useCallback((silent = false) => {
    const appId = typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_APP_ID_KEY) : null;
    if (!appId) {
      setTicketList([]);
      return Promise.resolve();
    }
    const base = getApiUrl('/api/tickets/').replace(/\/$/, '');
    if (!base) {
      setTicketList([]);
      return Promise.resolve();
    }
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    const headers = { accept: 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
    if (!silent) setTicketLoading(true);
    const url = `${base}/by-app/${appId}/as-staff/`;
    return axios
      .get(url, { headers, params: { page: 1, page_size: 100 } })
      .then(({ data }) => {
        const results = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
        const sorted = [...results].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        setTicketList(sorted.map(mapApiTicket));
      })
      .catch(() => setTicketList([]))
      .finally(() => { if (!silent) setTicketLoading(false); });
  }, []);

  const FETCH_INTERVAL_MS = 60000;
  useEffect(() => {
    fetchTickets(false);
    const iv = setInterval(() => fetchTickets(true), FETCH_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [fetchTickets]);

  useEffect(() => {
    setTablePage(0);
  }, [currentUser?.id, ticketList]);

  // รายการที่แจ้งใหม่ — แสดงกระดิ่งสีเหลือง + เล่นเสียงครั้งเดียวเมื่อมีรายการใหม่ (ไม่ใช้ localStorage)
  const prevIdsRef = useRef(new Set());
  useEffect(() => {
    if (!currentUser?.id) return;
    const newItems = ticketList || [];
    const ids = newItems.map((i) => i.id);
    const unacknowledgedIds = ids.filter((id) => !acknowledgedNewIdsRef.current.has(id));
    setNewReportIds(ids);
    const prevIds = prevIdsRef.current;
    const currentIds = new Set(ids);
    const newlyAdded = ids.filter((id) => !prevIds.has(id));
    prevIdsRef.current = currentIds;
    // hidden: กระดิ่งและเสียงเตือน
    // if (prevIds.size > 0 && newlyAdded.length > 0 && unacknowledgedIds.some((id) => newlyAdded.includes(id))) {
    //   playNewReportSound(alertSoundId);
    // }
  }, [currentUser?.id, ticketList, alertSoundId]);

  const paginatedHelpDeskList = relevantHelpDeskList.slice(
    tablePage * tableRowsPerPage,
    tablePage * tableRowsPerPage + tableRowsPerPage
  );

  const statusSummary = useMemo(() => {
    const list = ticketList || [];
    const pending = list.filter((t) => (t.status || 'pending') === 'pending').length;
    const forward = list.filter((t) => (t.status || '') === 'forward').length;
    const in_progress = list.filter((t) => (t.status || '') === 'in_progress').length;
    const completed = list.filter((t) => (t.status || '') === 'completed').length;
    const cancelled = list.filter((t) => (t.status || '') === 'cancelled').length;
    return { pending, forward, in_progress, completed, cancelled, total: list.length };
  }, [ticketList]);

  // eslint-disable-next-line no-unused-vars -- used in commented-out accept/complete UI
  const handleAccept = (itemId) => {
    setItemToAcceptId(itemId);
    setOpenAcceptConfirm(true);
  };

  // eslint-disable-next-line no-unused-vars -- used in commented-out complete UI
  const handleComplete = (itemId) => {
    setItemToCompleteId(itemId);
    setOpenCompleteConfirm(true);
  };

  const STATUS_THAI = useMemo(() => ({
    pending: 'รอรับเรื่อง',
    forward: 'ส่งต่อ',
    in_progress: 'กำลังดำเนินการ (รับเรื่องแล้ว)',
    completed: 'เสร็จสิ้น',
    cancelled: 'ยกเลิก',
  }), []);
  const statusToThai = (status) => STATUS_THAI[status] ?? status ?? '-';

  const STATUS_DISPLAY = useMemo(() => {
    const fallback = { bgcolor: 'rgba(0, 0, 0, 0.04)', color: 'rgba(0, 0, 0, 0.6)', icon: null };
    return {
      pending:     { bgcolor: 'rgba(237, 108, 2, 0.1)',   color: '#ed6c02', icon: <ScheduleIcon sx={{ fontSize: 16, color: 'inherit' }} /> },
      forward:     { bgcolor: 'rgba(2, 119, 189, 0.1)',   color: '#0277bd', icon: <ForwardIcon sx={{ fontSize: 16, color: 'inherit' }} /> },
      in_progress: { bgcolor: 'rgba(132, 94, 194, 0.1)', color: '#845EC2', icon: <AutorenewIcon sx={{ fontSize: 16, color: 'inherit' }} /> },
      completed:   { bgcolor: 'rgba(46, 125, 50, 0.1)',   color: '#2e7d32', icon: <CheckCircleIcon sx={{ fontSize: 16, color: 'inherit' }} /> },
      cancelled:   { bgcolor: 'rgba(0, 0, 0, 0.06)',     color: 'rgba(0, 0, 0, 0.6)', icon: <CancelIcon sx={{ fontSize: 16, color: 'inherit' }} /> },
      _fallback: fallback,
    };
  }, []);
  const getStatusDisplay = (status) => STATUS_DISPLAY[status] ?? STATUS_DISPLAY._fallback;
  const getStatusSelectValue = (item) => (item?.status ?? '');

  const triggerRowFieldFlash = useCallback((ticketId, kind) => {
    const id = Number(ticketId);
    if (Number.isNaN(id)) return;
    const prevTimer = rowFieldFlashTimersRef.current[id];
    if (prevTimer) clearTimeout(prevTimer);
    setRowFieldFlashById((prev) => ({ ...prev, [id]: kind }));
    rowFieldFlashTimersRef.current[id] = setTimeout(() => {
      setRowFieldFlashById((prev) => {
        const next = { ...prev };
        if (next[id] === kind) delete next[id];
        return next;
      });
      delete rowFieldFlashTimersRef.current[id];
    }, ROW_FIELD_FLASH_MS);
  }, []);

  useEffect(() => () => {
    Object.values(rowFieldFlashTimersRef.current).forEach((t) => clearTimeout(t));
  }, []);

  const handleUpdateStatus = useCallback(async (item, newStatus, description = '', department = null) => {
    if (!newStatus || (newStatus === item.status && newStatus !== 'forward')) return;
    const base = ticketsBase;
    if (!base) return;
    if (newStatus === 'forward' && (department == null || department === '')) return;
    setStatusUpdatingId(item.id);
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    const headers = { accept: 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
    const body = { status: newStatus };
    if (newStatus === 'forward' && department != null && department !== '') body.department = Number(department);
    if (description != null && String(description).trim() !== '') body.description = String(description).trim();
    try {
      await axios.patch(`${base}/${item.id}/`, body, { headers });
      const forwardedDepartmentName =
        newStatus === 'forward' && department != null && department !== ''
          ? (allDepartments.find((d) => Number(d?.id) === Number(department))?.name || item.department_name || '')
          : null;
      setTicketList((prev) =>
        prev.map((t) => (
          t.id === item.id
            ? {
              ...t,
              status: newStatus,
              ...(newStatus === 'forward' && department != null && department !== ''
                ? { department: Number(department), department_name: forwardedDepartmentName }
                : {}),
            }
            : t
        ))
      );
      triggerRowFieldFlash(item.id, 'status');
      setUpdateSnackbar({ open: true, severity: 'success', message: 'อัพเดตสถานะสำเร็จ' });
    } catch (err) {
      setUpdateSnackbar({ open: true, severity: 'error', message: err?.response?.data?.detail || err?.message || 'อัพเดตสถานะไม่สำเร็จ' });
    } finally {
      setStatusUpdatingId(null);
    }
  }, [ticketsBase, allDepartments, triggerRowFieldFlash]);

  const handleUpdatePriority = useCallback(async (item, newUrgency) => {
    const current = getPrioritySelectValue(item);
    if (newUrgency !== 'normal' && newUrgency !== 'high') return;
    if (newUrgency === current) return;
    const base = ticketsBase;
    if (!base) return;
    setPriorityUpdatingId(item.id);
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    const headers = { accept: 'application/json', 'Content-Type': 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
    try {
      await axios.patch(`${base}/${item.id}/`, { urgency: newUrgency }, { headers });
      setTicketList((prev) =>
        prev.map((t) => (t.id === item.id ? { ...t, urgency: newUrgency } : t))
      );
      triggerRowFieldFlash(item.id, 'urgency');
      setUpdateSnackbar({ open: true, severity: 'success', message: 'อัพเดตความเร่งด่วนสำเร็จ' });
    } catch (err) {
      setUpdateSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.detail || err?.message || 'อัพเดตความเร่งด่วนไม่สำเร็จ',
      });
    } finally {
      setPriorityUpdatingId(null);
    }
  }, [ticketsBase, triggerRowFieldFlash]);

  const openStatusConfirm = (item, newStatus) => {
    if (!newStatus || (newStatus === item.status && newStatus !== 'forward')) return;
    setStatusConfirmItem(item);
    setStatusConfirmNewStatus(newStatus);
    setStatusConfirmDescription('');
    setStatusConfirmDepartment('');
    setStatusConfirmOpen(true);
  };

  const handleStatusConfirmClose = () => {
    setStatusConfirmOpen(false);
    setStatusConfirmItem(null);
    setStatusConfirmNewStatus('');
    setStatusConfirmDescription('');
    setStatusConfirmDepartment('');
  };

  const handleStatusConfirmSubmit = () => {
    if (!statusConfirmItem || !statusConfirmNewStatus) return;
    if (statusConfirmNewStatus === 'forward' && !statusConfirmDepartment) return;
    handleUpdateStatus(statusConfirmItem, statusConfirmNewStatus, statusConfirmDescription, statusConfirmNewStatus === 'forward' ? statusConfirmDepartment : null);
    handleStatusConfirmClose();
  };

  const getHelpDeskCategoryLabel = (category) => {
    return category === 'water' ? 'ด้านประปา' : category === 'electricity' ? 'ด้านไฟฟ้า' : 'ด้าน อื่นๆ';
  };

  const getHelpDeskCategoryChipStyle = (category) => {
    if (!category) return { bgcolor: 'rgba(0, 0, 0, 0.06)', color: 'rgba(0, 0, 0, 0.65)', borderColor: 'rgba(0, 0, 0, 0.12)' };
    if (category === 'water') return { bgcolor: 'rgba(30, 144, 255, 0.10)', color: '#1e90ff', borderColor: 'rgba(30, 144, 255, 0.25)' };
    if (category === 'electricity') return { bgcolor: 'rgba(255, 152, 0, 0.10)', color: '#ff9800', borderColor: 'rgba(255, 152, 0, 0.25)' };
    return { bgcolor: 'rgba(132, 94, 194, 0.10)', color: '#845EC2', borderColor: 'rgba(132, 94, 194, 0.25)' };
  };

  const getHelpDeskCategoryIcon = (category) => {
    if (category === 'water') return <WaterDropIcon sx={{ fontSize: 18 }} />;
    if (category === 'electricity') return <BoltIcon sx={{ fontSize: 18 }} />;
    if (category === 'cooperative') return <GroupIcon sx={{ fontSize: 18 }} />;
    return <CategoryIcon sx={{ fontSize: 18 }} />;
  };

  const getDepartmentDisplay = (dept) => {
    const name = dept?.name || '';
    if (/ประปา|น้ำ/i.test(name)) return { Icon: WaterDropIcon, color: '#1e90ff', borderColor: 'rgba(30, 144, 255, 0.1)', hoverBorder: 'rgba(30, 144, 255, 0.5)', hoverBg: 'rgba(30, 144, 255, 0.1)', hoverShadow: '0 8px 24px rgba(30, 144, 255, 0.3)' };
    if (/ไฟฟ้า/i.test(name)) return { Icon: BoltIcon, color: '#ff9800', borderColor: 'rgba(255, 152, 0, 0.1)', hoverBorder: 'rgba(255, 152, 0, 0.5)', hoverBg: 'rgba(255, 152, 0, 0.1)', hoverShadow: '0 8px 24px rgba(255, 152, 0, 0.3)' };
    return { Icon: GroupIcon, color: '#845EC2', borderColor: 'rgba(132, 94, 194, 0.1)', hoverBorder: 'rgba(132, 94, 194, 0.5)', hoverBg: 'rgba(132, 94, 194, 0.1)', hoverShadow: '0 8px 24px rgba(132, 94, 194, 0.3)' };
  };

  const getCategoryDisplay = (departmentName) => getDepartmentDisplay({ name: departmentName });

  const isNewReport = (item) =>
    newReportIds.includes(item.id) && !acknowledgedNewIdsRef.current.has(item.id);

  // hidden: กระดิ่งและเสียงเตือน
  const renderNewReportBell = (item) => {
    return null; // hidden: ไม่แสดงไอคอนกระดิ่ง
    // if (!isNewReport(item)) return null;
    /* return (
      <Tooltip title="มีรายการใหม่ถูกแจ้งมา">
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            verticalAlign: 'middle',
            ml: 0.5,
            animation: `${shakeKeyframes} 0.5s ease-in-out infinite`,
          }}
        >
          <NotificationsActiveIcon sx={{ color: '#f9a825', fontSize: 22 }} />
        </Box>
      </Tooltip>
    ); */
  };

  const renderAttachmentIndicators = (item) => {
    const apiAttachments = Array.isArray(item?.attachments) ? item.attachments : [];
    const legacyImages = Array.isArray(item?.images) ? item.images.length : 0;
    const imageCount = legacyImages || apiAttachments.filter((a) => /\.(png|jpe?g|gif|webp)(\?|$)/i.test(String(a?.file || '').split('/').pop() || '')).length;
    const fileCount = apiAttachments.filter((a) => !/\.(png|jpe?g|gif|webp)(\?|$)/i.test(String(a?.file || '').split('/').pop() || '')).length;
    const hasImages = imageCount > 0;
    const hasFiles = fileCount > 0;
    const activeColor = '#845EC2';
    const disabledColor = 'rgba(0,0,0,0.26)';

    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          ml: 0.75,
          verticalAlign: 'middle',
          whiteSpace: 'nowrap',
        }}
      >
        <Tooltip title={hasImages ? 'มีรูปแนบ' : 'ไม่มีรูปแนบ'}>
          <Box component="span" sx={{ display: 'inline-flex', color: hasImages ? activeColor : disabledColor }}>
            <ImageIcon sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>
        <Tooltip title={hasFiles ? 'มีไฟล์แนบ' : 'ไม่มีไฟล์แนบ'}>
          <Box component="span" sx={{ display: 'inline-flex', color: hasFiles ? activeColor : disabledColor }}>
            <AttachFileIcon sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>
      </Box>
    );
  };

  const handleOpenHelpDeskDetail = (item) => {
    if (item?.id != null && newReportIds.includes(item.id)) {
      acknowledgedNewIdsRef.current.add(item.id);
    }
    navigate(`/ticket?id=${item?.id ?? ''}`);
  };

  // hidden: กระดิ่งและเสียงเตือน
  const handleAlertSoundChange = (e) => {
    const id = e.target.value;
    setAlertSoundId(id);
    try {
      localStorage.setItem(ALERT_SOUND_KEY, id);
    } catch (_) {}
    // playNewReportSound(id);
  };

  // eslint-disable-next-line no-unused-vars -- used in commented-out SLA chip
  const getSLAStatus = (item) => {
    if (checkSLAViolation(item)) {
      return { label: 'เกิน SLA', color: 'error' };
    }
    return { label: 'ปกติ', color: 'success' };
  };

  const hasSelectedApp = typeof localStorage !== 'undefined' && !!localStorage.getItem(SELECTED_APP_ID_KEY);

  return (
    <Box sx={{ px: { xs: 2, sm: 0 }, pb: { xs: 3, sm: 0 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        {hasSelectedApp && (
          <Typography variant="h6" sx={{ color: 'rgba(0, 0, 0, 0.6)', mb: 1.5, fontWeight: 600, textAlign: 'center' }}>
            APP : {displayAppName != null ? (displayAppName || '-') : 'กำลังโหลด...'}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'center', sm: 'space-between' },
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            gap: { xs: 1.25, sm: 2 },
            mb: 2,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.5)', textAlign: 'left', width: { xs: '100%', sm: 'auto' }, fontSize: '0.8125rem' }}>
            {authVerifyLoading ? 'กำลังโหลด...' : (
              <>
                ยินดีต้อนรับ,{' '}
                <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                  {authVerifyUser?.full_name || currentUser?.full_name || currentUser?.username || '-'}
                </Box>
              </>
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, justifyContent: { xs: 'stretch', sm: 'flex-start' }, width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                localStorage.removeItem(SELECTED_APP_ID_KEY);
                localStorage.removeItem(SELECTED_APP_STAFF_KEY);
                localStorage.removeItem(SELECTED_APP_NAME_KEY);
                localStorage.removeItem(SELECTED_APP_USER_HAS_ACCESS_KEY);
                localStorage.removeItem(STAFF_DEPARTMENT_IDS_KEY);
                navigate('/select-app');
              }}
              sx={{
                borderRadius: 1,
                minHeight: { xs: 44, sm: 36 },
                width: { xs: '100%', sm: 'auto' },
                borderColor: 'rgba(132, 94, 194, 0.5)',
                color: '#845EC2',
                '&:hover': { borderColor: '#845EC2', bgcolor: 'rgba(132, 94, 194, 0.08)' },
              }}
            >
              ย้อนกลับไปเลือกแอป
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Dashboard สรุปสถานะ */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
          width: '100%',
        }}
      >
        {[
          { key: 'pending', label: 'รอรับเรื่อง', count: statusSummary.pending },
          { key: 'forward', label: 'ส่งต่อ', count: statusSummary.forward },
          { key: 'in_progress', label: 'กำลังดำเนินการ', count: statusSummary.in_progress },
          { key: 'completed', label: 'เสร็จสิ้น', count: statusSummary.completed },
          { key: 'cancelled', label: 'ยกเลิก', count: statusSummary.cancelled },
          { key: 'total', label: 'ทั้งหมด', count: statusSummary.total },
        ].map(({ key, label, count }) => {
          const statusDisplay = key === 'total' ? null : getStatusDisplay(key);
          const accentColor = key === 'total'
            ? '#845EC2'
            : (statusDisplay?.color || '#845EC2');
          const accentSoft = key === 'total'
            ? 'rgba(132, 94, 194, 0.14)'
            : (statusDisplay?.bgcolor || 'rgba(0,0,0,0.06)');
          const iconNode = key === 'total'
            ? <FormatListBulletedIcon sx={{ fontSize: 22 }} />
            : (statusDisplay?.icon || null);
          const grad = `linear-gradient(90deg, ${accentColor} 0%, rgba(255,255,255,0) 70%)`;

          return (
            <Box
              key={key}
              sx={{
                flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.333% - 11px)', md: '1 1 0%' },
                minWidth: 0,
              }}
            >
              <Card
                elevation={0}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.78)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  boxShadow: '0 10px 26px rgba(132, 94, 194, 0.10), 0 2px 6px rgba(0,0,0,0.06)',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  height: '100%',
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 14px 34px rgba(132, 94, 194, 0.14), 0 4px 10px rgba(0,0,0,0.06)',
                    borderColor: 'rgba(132, 94, 194, 0.18)',
                  },
                }}
              >
                {/* top highlight */}
                <Box sx={{ height: 4, width: '100%', background: grad, opacity: 0.9 }} />

                <CardContent
                  sx={{
                    py: 2,
                    px: 2.25,
                    minHeight: { xs: 96, sm: 104 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.55)',
                        fontWeight: 600,
                        mb: 0.75,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '0.75rem',
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        color: accentColor,
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.05,
                      }}
                    >
                      {count}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                      รายการ
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      flex: '0 0 auto',
                      width: 46,
                      height: 46,
                      borderRadius: 999,
                      bgcolor: accentSoft,
                      color: accentColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(0,0,0,0.04)',
                    }}
                  >
                    {iconNode}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>

      {/* HelpDesk Table – theme เดียวกับ ReporterDashboard, มือถือเรียงแนวตั้ง */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            การแจ้งเหตุ-ข้อขัดข้อง
          </Typography>
          
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 1,
            alignItems: 'stretch',
            width: { xs: '100%', md: 'auto' },
            maxWidth: { xs: '100%', md: 'none' },
            p: { xs: 1.5, md: 0 },
            borderRadius: 2,
            bgcolor: { xs: 'rgba(255, 255, 255, 0.9)', md: 'transparent' },
            border: { xs: '1px solid rgba(0, 0, 0, 0.08)', md: 'none' },
            boxShadow: { xs: '0 2px 8px rgba(0, 0, 0, 0.06)', md: 'none' },
          }}
        >
          {/* hidden: กระดิ่งและเสียงเตือน
          <FormControl size="small" sx={{ minWidth: { md: 160 }, width: { xs: '100%', md: 'auto' } }} variant="outlined">
            <InputLabel id="alert-sound-label">เสียงเตือน</InputLabel>
            <Select
              labelId="alert-sound-label"
              value={alertSoundId}
              onChange={handleAlertSoundChange}
              label="เสียงเตือน"
              sx={{ borderRadius: 2, bgcolor: 'background.paper' }}
            >
              {SOUND_PRESETS.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          */}
          {canShowManageButton && (
            <Button
              variant="outlined"
              startIcon={<ManageAccountsIcon />}
              onClick={() => navigate('/manage-members')}
              sx={{
                borderRadius: 2,
                color: '#845EC2',
                borderColor: '#845EC2',
                minHeight: 40,
                width: { xs: '100%', md: 'auto' },
                flexShrink: 0,
                '&:hover': { borderColor: '#6344A4', bgcolor: 'rgba(132, 94, 194, 0.08)' }
              }}
            >
              จัดการผู้ดูแล
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<FormatListBulletedIcon />}
            onClick={() => navigate('/notifications')}
            sx={{
              borderRadius: 2,
              color: '#845EC2',
              borderColor: '#845EC2',
              minHeight: 40,
              width: { xs: '100%', md: 'auto' },
              flexShrink: 0,
              '&:hover': { borderColor: '#6344A4', bgcolor: 'rgba(132, 94, 194, 0.08)' }
            }}
          >
            รายการแจ้งเตือน / ประกาศ
          </Button>
        </Box>
      </Box>

      {/* แถบตัวกรอง: ค้นหา, ประเภท, สถานะ, ล้าง */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'stretch',
          gap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          placeholder="ค้นหา เรื่อง, รายละเอียด"
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(0, 0, 0, 0.4)', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: { xs: '1 1 100%', sm: '1 1 240px' },
            minWidth: { xs: '100%', sm: 200 },
            '& .MuiOutlinedInput-root': {
              bgcolor: '#fff',
              borderRadius: 2,
              '& fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
              '&:hover fieldset': { borderColor: 'rgba(132, 94, 194, 0.6)' },
              '&.Mui-focused fieldset': { borderColor: '#845EC2', borderWidth: 1 },
            },
          }}
        />
        <FormControl
          size="small"
          sx={{
            minWidth: { xs: '100%', sm: 140 },
            '& .MuiOutlinedInput-root': {
              bgcolor: '#fff',
              borderRadius: 2,
              '& fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
              '&:hover fieldset': { borderColor: 'rgba(132, 94, 194, 0.6)' },
              '&.Mui-focused fieldset': { borderColor: '#845EC2', borderWidth: 1 },
            },
          }}
          variant="outlined"
        >
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            displayEmpty
            renderValue={(v) => FILTER_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? 'ประเภท'}
            sx={{ fontSize: '0.875rem' }}
          >
            {FILTER_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          size="small"
          sx={{
            minWidth: { xs: '100%', sm: 160 },
            '& .MuiOutlinedInput-root': {
              bgcolor: '#fff',
              borderRadius: 2,
              '& fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
              '&:hover fieldset': { borderColor: 'rgba(132, 94, 194, 0.6)' },
              '&.Mui-focused fieldset': { borderColor: '#845EC2', borderWidth: 1 },
            },
          }}
          variant="outlined"
        >
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            displayEmpty
            renderValue={(v) => FILTER_STATUS_OPTIONS.find((o) => o.value === v)?.label ?? 'สถานะ'}
            sx={{ fontSize: '0.875rem' }}
          >
            {FILTER_STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          onClick={() => {
            setFilterSearch('');
            setFilterType('');
            setFilterStatus('');
          }}
          sx={{
            borderRadius: 2,
            color: '#845EC2',
            borderColor: 'rgba(132, 94, 194, 0.5)',
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            minWidth: { xs: '100%', sm: 80 },
            '&:hover': { borderColor: '#845EC2', bgcolor: 'rgba(132, 94, 194, 0.06)' },
          }}
        >
          ล้าง
        </Button>
      </Box>

      {/* Table view: md+ (เหมือน ReporterDashboard) */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <TableContainer
          component={Paper}
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(to right, rgb(48, 176, 235), rgba(132, 94, 194, 0.8), rgb(143, 212, 244))' }}>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">ลำดับ</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>เรื่อง</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>ผู้แจ้ง</TableCell>
                {showCategoryField && (
                  <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>ประเภท</TableCell>
                )}
                <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>หน่วยงาน</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>สถานที่</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">สถานะ</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">สร้างเมื่อ</TableCell>

                {/* <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">สถานะ</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">SLA</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">สร้างเมื่อ</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">รับเมื่อ</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">เสร็จเมื่อ</TableCell> */}
                <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">การดำเนินการ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ticketLoading && relevantHelpDeskList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showCategoryField ? 9 : 8} align="center" sx={{ color: 'rgba(0, 0, 0, 0.5)', py: 4 }}>
                    กำลังโหลด...
                  </TableCell>
                </TableRow>
              ) : relevantHelpDeskList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showCategoryField ? 9 : 8} align="center" sx={{ color: 'rgba(0, 0, 0, 0.5)', py: 4 }}>
                    ไม่มีรายการ
                  </TableCell>
                </TableRow>
              ) : (
                paginatedHelpDeskList.map((item, index) => {
                  const rowFlashKind = rowFieldFlashById[item.id];
                  const rowFlashSx = rowFlashKind
                    ? {
                      bgcolor: rowFlashKind === 'urgency' ? 'rgba(132, 94, 194, 0.18)' : 'rgba(237, 108, 2, 0.14)',
                      transition: 'background-color 0.35s ease',
                      animation: `${shakeKeyframes} 0.45s ease-in-out 2`,
                    }
                    : {};
                  // const slaStatus = getSLAStatus(item);
                  return (
                    <TableRow
                      key={item.id}
                      hover
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        ...rowFlashSx,
                      }}
                    >
                      <TableCell sx={{ color: 'rgba(0, 0, 0, 0.7)' }} align="center">
                        {tablePage * tableRowsPerPage + index + 1}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                          {renderNewReportBell(item)}
                          <span>{item.title}</span>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>{item.owner_fullname || item.owner_username || item.reporterName || '-'}</TableCell>
                      {showCategoryField && (
                        <TableCell>
                          <Chip
                            icon={getHelpDeskCategoryIcon(item.category)}
                            label={getHelpDeskCategoryLabel(item.category)}
                            size="small"
                            sx={{
                              border: '1px solid',
                              '& .MuiChip-icon': { color: 'inherit' },
                              ...getHelpDeskCategoryChipStyle(item.category),
                              borderRadius: 2,
                              fontWeight: 700
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                        {item.department_name || '-'}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>{item.location || '-'}</TableCell>
                      <TableCell align="center">
                        {(() => {
                          const s = getStatusDisplay(item.status);
                          return (
                            <Chip
                              icon={s.icon}
                              label={statusToThai(item.status)}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                borderRadius: '9999px',
                                bgcolor: s.bgcolor,
                                color: s.color,
                                fontSize: '0.75rem',
                                '& .MuiChip-icon': { color: 'inherit' },
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                      {/* <TableCell align="center">
                        <Chip label={slaStatus.label} color={slaStatus.color} size="small" sx={{ borderRadius: 2 }} />
                      </TableCell> */}
                      {/* <TableCell sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.875rem' }} align="center">{formatDate(item.createdAt)}</TableCell>
                      <TableCell sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.875rem' }} align="center">{item.acceptedAt ? formatDate(item.acceptedAt) : '-'}</TableCell>
                      <TableCell sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.875rem' }} align="center">{item.completedAt ? formatDate(item.completedAt) : '-'}</TableCell> */}
                      <TableCell sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.875rem' }} align="center">{formatDate(item.createdAt)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Tooltip title="ดูรายละเอียด">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenHelpDeskDetail(item)}
                              sx={{
                                borderRadius: 2,
                                minWidth: { xs: 40, md: 168 },
                                height: 36,
                                px: { xs: 1, md: 1.5 },
                                justifyContent: 'center',
                                color: '#845EC2',
                                borderColor: '#845EC2',
                                '&:hover': { borderColor: '#6344A4', bgcolor: 'rgba(132, 94, 194, 0.08)' },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' }, ml: 0.5 }}>ดูรายละเอียด</Box>
                            </Button>
                          </Tooltip>
                          <FormControl
                            size="small"
                            sx={{
                              minWidth: 168,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                fontSize: '0.8125rem',
                                fontWeight: 500,
                                bgcolor: 'rgba(255, 255, 255, 0.95)',
                                borderColor: 'rgba(132, 94, 194, 0.35)',
                                '&:hover': { borderColor: 'rgba(132, 94, 194, 0.6)', bgcolor: '#fff' },
                                '&.Mui-focused': { borderColor: '#845EC2', boxShadow: '0 0 0 2px rgba(132, 94, 194, 0.2)' },
                                '& fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
                              },
                            }}
                            variant="outlined"
                          >
                            <InputLabel id={`priority-select-${item.id}`}>ความเร่งด่วน</InputLabel>
                            <Select
                              labelId={`priority-select-${item.id}`}
                              label="ความเร่งด่วน"
                              value={getPrioritySelectValue(item)}
                              onChange={(e) => handleUpdatePriority(item, e.target.value)}
                              disabled={priorityUpdatingId === item.id || statusUpdatingId === item.id}
                              sx={{ height: 36, '& .MuiSelect-select': { py: 1, px: 1.5 } }}
                              renderValue={(v) =>
                                priorityUpdatingId === item.id
                                  ? '...'
                                  : (PRIORITY_OPTIONS.find((o) => o.value === v)?.label ?? v)
                              }
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    mt: 1,
                                    borderRadius: 2,
                                    boxShadow: '0 4px 20px rgba(132, 94, 194, 0.15)',
                                    border: '1px solid rgba(132, 94, 194, 0.12)',
                                    '& .MuiMenuItem-root': { fontSize: '0.8125rem', py: 1.25 },
                                  },
                                },
                              }}
                            >
                              {PRIORITY_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl
                            size="small"
                            sx={{
                              minWidth: 168,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                fontSize: '0.8125rem',
                                fontWeight: 500,
                                bgcolor: 'rgba(255, 255, 255, 0.95)',
                                borderColor: 'rgba(132, 94, 194, 0.35)',
                                '&:hover': { borderColor: 'rgba(132, 94, 194, 0.6)', bgcolor: '#fff' },
                                '&.Mui-focused': { borderColor: '#845EC2', boxShadow: '0 0 0 2px rgba(132, 94, 194, 0.2)' },
                                '& fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
                              },
                            }}
                            variant="outlined"
                          >
                            <InputLabel id={`status-select-${item.id}`}>สถานะ</InputLabel>
                            <Select
                              labelId={`status-select-${item.id}`}
                              label="สถานะ"
                              value={getStatusSelectValue(item)}
                              onChange={(e) => openStatusConfirm(item, e.target.value === '__forward_again__' ? 'forward' : e.target.value)}
                              disabled={statusUpdatingId === item.id || priorityUpdatingId === item.id}
                              displayEmpty
                              sx={{ height: 36, '& .MuiSelect-select': { py: 1, px: 1.5 } }}
                              renderValue={(v) => statusUpdatingId === item.id ? '...' : (STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v)}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    mt: 1,
                                    borderRadius: 2,
                                    boxShadow: '0 4px 20px rgba(132, 94, 194, 0.15)',
                                    border: '1px solid rgba(132, 94, 194, 0.12)',
                                    '& .MuiMenuItem-root': { fontSize: '0.8125rem', py: 1.25 },
                                    '& .MuiMenuItem-root.Mui-selected': { bgcolor: 'rgba(132, 94, 194, 0.08)' },
                                    '& .MuiMenuItem-root.Mui-selected:hover': { bgcolor: 'rgba(132, 94, 194, 0.12)' },
                                  },
                                },
                              }}
                            >
                              {STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              ))}
                              {item.status === 'forward' && (
                                <MenuItem
                                  value="__forward_again__"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#845EC2',
                                    bgcolor: 'rgba(132, 94, 194, 0.12)',
                                    '&:hover': { bgcolor: 'rgba(132, 94, 194, 0.2)' },
                                  }}
                                >
                                  ส่งต่ออีกครั้ง
                                </MenuItem>
                              )}
                            </Select>
                          </FormControl>
                          
                          {/* {item.status === .PENDING && (
                            <Tooltip title="รับเรื่อง">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleAccept(item.id)}
                                sx={{ borderRadius: 2, minWidth: 40, px: 1, color: '#2196f3', borderColor: '#2196f3', '&:hover': { borderColor: '#1976d2', bgcolor: 'rgba(33, 150, 243, 0.08)' } }}
                              >
                                <AssignmentIcon fontSize="small" />
                              </Button>
                            </Tooltip>
                          )}
                          {item.status === 'accepted' && (
                            <Tooltip title="ทำสำเร็จแล้ว">
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                onClick={() => handleComplete(item.id)}
                                sx={{ borderRadius: 2, minWidth: 40, px: 1 }}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </Button>
                            </Tooltip>
                          )} */}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={relevantHelpDeskList.length}
          page={tablePage}
          onPageChange={(_, newPage) => setTablePage(newPage)}
          rowsPerPage={tableRowsPerPage}
          onRowsPerPageChange={(e) => {
            setTableRowsPerPage(parseInt(e.target.value, 10));
            setTablePage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="จำนวนแถวต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
          sx={{
            borderTop: '1px solid rgba(0,0,0,0.08)',
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '0 0 8px 8px',
            '.MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-end' } }
          }}
        />
      </Box>

      {/* Card view: xs/sm (เหมือน ReporterDashboard) */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {ticketLoading && relevantHelpDeskList.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}
          >
            <CardContent sx={{ py: 4 }}>
              <Typography align="center" sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                กำลังโหลด...
              </Typography>
            </CardContent>
          </Card>
        ) : relevantHelpDeskList.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}
          >
            <CardContent sx={{ py: 4 }}>
              <Typography align="center" sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                ไม่มีรายการ
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {paginatedHelpDeskList.map((item) => {
              const rowFlashKind = rowFieldFlashById[item.id];
              const rowFlashSx = rowFlashKind
                ? {
                  bgcolor: rowFlashKind === 'urgency' ? 'rgba(132, 94, 194, 0.18)' : 'rgba(237, 108, 2, 0.14)',
                  transition: 'background-color 0.35s ease',
                  animation: `${shakeKeyframes} 0.45s ease-in-out 2`,
                }
                : {};
              return (
                <Grid item xs={12} key={item.id}>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.92)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 3,
                      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
                      border: '1px solid rgba(0, 0, 0, 0.06)',
                      ...rowFlashSx,
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            {renderNewReportBell(item)}
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 800,
                                color: 'rgba(0,0,0,0.78)',
                                lineHeight: 1.2,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}
                            >
                              {item.title}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(0,0,0,0.55)',
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={item.owner_fullname || item.owner_username || item.reporterName || '-'}
                          >
                            ผู้แจ้ง: {item.owner_fullname || item.owner_username || item.reporterName || '-'}
                          </Typography>
                        </Box>
                        {(() => {
                          const s = getStatusDisplay(item.status);
                          return (
                            <Chip
                              icon={s.icon}
                              label={statusToThai(item.status)}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                borderRadius: '9999px',
                                bgcolor: s.bgcolor,
                                color: s.color,
                                fontSize: '0.75rem',
                                '& .MuiChip-icon': { color: 'inherit' },
                              }}
                            />
                          );
                        })()}
                        {showCategoryField && (
                          <Chip
                            icon={getHelpDeskCategoryIcon(item.category)}
                            label={getHelpDeskCategoryLabel(item.category)}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              borderRadius: 2,
                              border: '1px solid',
                              '& .MuiChip-icon': { color: 'inherit' },
                              ...getHelpDeskCategoryChipStyle(item.category),
                            }}
                          />
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                        <Chip label={`สถานที่: ${item.location || '-'}`} size="small" sx={{ borderRadius: 2 }} />
                      </Box>

                      <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.55)', display: 'block' }}>
                        สร้างเมื่อ: {formatDate(item.createdAt)}
                      </Typography>
                      {/* <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.55)', display: 'block' }}>
                        รับเมื่อ: {item.acceptedAt ? formatDate(item.acceptedAt) : '-'} | เสร็จเมื่อ: {item.completedAt ? formatDate(item.completedAt) : '-'}
                      </Typography> */}

                      <Box sx={{ mt: 1.5, mb: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        <FormControl
                          size="small"
                          fullWidth
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              fontSize: '0.8125rem',
                              fontWeight: 500,
                              bgcolor: 'rgba(255, 255, 255, 0.95)',
                              borderColor: 'rgba(132, 94, 194, 0.35)',
                              '&:hover': { borderColor: 'rgba(132, 94, 194, 0.6)', bgcolor: '#fff' },
                              '&.Mui-focused': { borderColor: '#845EC2', boxShadow: '0 0 0 2px rgba(132, 94, 194, 0.2)' },
                              '& fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
                            },
                          }}
                        >
                          <InputLabel id={`priority-select-mobile-${item.id}`}>ความเร่งด่วน</InputLabel>
                          <Select
                            labelId={`priority-select-mobile-${item.id}`}
                            label="ความเร่งด่วน"
                            value={getPrioritySelectValue(item)}
                            onChange={(e) => handleUpdatePriority(item, e.target.value)}
                            disabled={priorityUpdatingId === item.id || statusUpdatingId === item.id}
                            sx={{ height: 40, '& .MuiSelect-select': { py: 1.25, px: 1.5 } }}
                            renderValue={(v) =>
                              priorityUpdatingId === item.id
                                ? '...'
                                : (PRIORITY_OPTIONS.find((o) => o.value === v)?.label ?? v)
                            }
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  mt: 1,
                                  borderRadius: 2,
                                  boxShadow: '0 4px 20px rgba(132, 94, 194, 0.15)',
                                  border: '1px solid rgba(132, 94, 194, 0.12)',
                                  '& .MuiMenuItem-root': { fontSize: '0.8125rem', py: 1.25 },
                                },
                              },
                            }}
                          >
                            {PRIORITY_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl
                          size="small"
                          fullWidth
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              fontSize: '0.8125rem',
                              fontWeight: 500,
                              bgcolor: 'rgba(255, 255, 255, 0.95)',
                              borderColor: 'rgba(132, 94, 194, 0.35)',
                              '&:hover': { borderColor: 'rgba(132, 94, 194, 0.6)', bgcolor: '#fff' },
                              '&.Mui-focused': { borderColor: '#845EC2', boxShadow: '0 0 0 2px rgba(132, 94, 194, 0.2)' },
                              '& fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
                            },
                          }}
                        >
                          <InputLabel id={`status-select-mobile-${item.id}`}>สถานะ</InputLabel>
                          <Select
                            labelId={`status-select-mobile-${item.id}`}
                            label="สถานะ"
                            value={getStatusSelectValue(item)}
                            onChange={(e) => openStatusConfirm(item, e.target.value === '__forward_again__' ? 'forward' : e.target.value)}
                            disabled={statusUpdatingId === item.id || priorityUpdatingId === item.id}
                            displayEmpty
                            sx={{ height: 40, '& .MuiSelect-select': { py: 1.25, px: 1.5 } }}
                            renderValue={(v) => statusUpdatingId === item.id ? '...' : (STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v)}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  mt: 1,
                                  borderRadius: 2,
                                  boxShadow: '0 4px 20px rgba(132, 94, 194, 0.15)',
                                  border: '1px solid rgba(132, 94, 194, 0.12)',
                                  '& .MuiMenuItem-root': { fontSize: '0.8125rem', py: 1.25 },
                                  '& .MuiMenuItem-root.Mui-selected': { bgcolor: 'rgba(132, 94, 194, 0.08)' },
                                  '& .MuiMenuItem-root.Mui-selected:hover': { bgcolor: 'rgba(132, 94, 194, 0.12)' },
                                },
                              },
                            }}
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                            {item.status === 'forward' && (
                              <MenuItem
                                value="__forward_again__"
                                sx={{
                                  fontWeight: 700,
                                  color: '#845EC2',
                                  bgcolor: 'rgba(132, 94, 194, 0.12)',
                                  '&:hover': { bgcolor: 'rgba(132, 94, 194, 0.2)' },
                                }}
                              >
                                ส่งต่ออีกครั้ง
                              </MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<VisibilityIcon fontSize="small" />}
                          onClick={() => handleOpenHelpDeskDetail(item)}
                          sx={{ borderRadius: 2, minHeight: 44, color: '#845EC2', borderColor: 'rgba(132, 94, 194, 0.6)' }}
                        >
                          รายละเอียด
                        </Button>
                        {/* {item.status === 'pending' && (
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<AssignmentIcon fontSize="small" />}
                            onClick={() => handleAccept(item.id)}
                            sx={{ borderRadius: 2, minHeight: 44, color: '#2196f3', borderColor: 'rgba(33, 150, 243, 0.6)' }}
                          >
                            รับเรื่อง
                          </Button>
                        )} */}
                        {/* {item.status === 'accepted' && (
                          <Button
                            fullWidth
                            variant="outlined"
                            color="success"
                            startIcon={<CheckCircleIcon fontSize="small" />}
                            onClick={() => handleComplete(item.id)}
                            sx={{ borderRadius: 2, minHeight: 44 }}
                          >
                            ทำสำเร็จ
                          </Button>
                        )} */}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        <TablePagination
          component="div"
          count={relevantHelpDeskList.length}
          page={tablePage}
          onPageChange={(_, newPage) => setTablePage(newPage)}
          rowsPerPage={tableRowsPerPage}
          onRowsPerPageChange={(e) => {
            setTableRowsPerPage(parseInt(e.target.value, 10));
            setTablePage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="จำนวนแถวต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
          sx={{
            borderTop: '1px solid rgba(0,0,0,0.08)',
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 2,
            mt: 1,
            '.MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-end' } }
          }}
        />
      </Box>

      {/* Dialog ยืนยันรับเรื่อง */}
      <Dialog open={openAcceptConfirm} onClose={() => { setOpenAcceptConfirm(false); setItemToAcceptId(null); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>ยืนยันการรับเรื่อง</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 0.5 }}>
            <AlertTitle sx={{ fontSize: '0.9375rem' }}>ต้องการรับเรื่องนี้ใช่หรือไม่?</AlertTitle>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setOpenAcceptConfirm(false); setItemToAcceptId(null); }} sx={{ borderRadius: 1 }}>ยกเลิก</Button>
          <Button variant="contained" onClick={() => { if (itemToAcceptId) { acceptHelpDesk(itemToAcceptId, currentUser); setOpenAcceptConfirm(false); setItemToAcceptId(null); } }} sx={{ borderRadius: 1, bgcolor: '#2196f3', '&:hover': { bgcolor: '#1976d2' } }}>รับเรื่อง</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog ยืนยันทำสำเร็จ */}
      <Dialog open={openCompleteConfirm} onClose={() => { setOpenCompleteConfirm(false); setItemToCompleteId(null); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>ยืนยันการทำสำเร็จ</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mt: 0.5 }}>
            <AlertTitle sx={{ fontSize: '0.9375rem' }}>ยืนยันว่าทำเรื่องนี้สำเร็จแล้ว?</AlertTitle>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setOpenCompleteConfirm(false); setItemToCompleteId(null); }} sx={{ borderRadius: 1 }}>ยกเลิก</Button>
          <Button variant="contained" color="success" onClick={() => { if (itemToCompleteId) { completeHelpDesk(itemToCompleteId, currentUser); setOpenCompleteConfirm(false); setItemToCompleteId(null); } }} sx={{ borderRadius: 1 }}>ยืนยัน</Button>
        </DialogActions>
      </Dialog>

      {/* Popup แสดงรูป (ไม่เปิด tab ใหม่) - เหมือน ReporterDashboard */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogContent sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
          {imagePreviewUrls.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`รูป ${i + 1}`}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          ))}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={() => setImagePreviewOpen(false)} variant="contained" sx={{ borderRadius: 2 }}>
            ปิด
          </Button>
        </DialogActions>
      </Dialog>

      {/* ยืนยันเปลี่ยนสถานะ + คำอธิบาย (ไม่บังคับ) */}
      <Dialog
        open={statusConfirmOpen}
        onClose={handleStatusConfirmClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>ยืนยันการเปลี่ยนสถานะ</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', mb: 2 }}>
            เปลี่ยนสถานะเป็น <strong>{statusConfirmNewStatus ? statusToThai(statusConfirmNewStatus) : '-'}</strong>
          </Typography>
          {statusConfirmNewStatus === 'forward' && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="status-confirm-department-label">แผนก (ส่งต่อถึง)</InputLabel>
              <Select
                labelId="status-confirm-department-label"
                label="แผนก (ส่งต่อถึง)"
                value={statusConfirmDepartment}
                onChange={(e) => setStatusConfirmDepartment(e.target.value)}
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(132, 94, 194, 0.35)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(132, 94, 194, 0.6)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#845EC2' },
                }}
              >
                {allDepartments.map((d) => (
                  <MenuItem
                    key={d.id}
                    value={d.id}
                    disabled={statusConfirmItem?.department != null && Number(d.id) === Number(statusConfirmItem.department)}
                  >
                    {d.name ?? `แผนก ${d.id}`}
                    {statusConfirmItem?.department != null && Number(d.id) === Number(statusConfirmItem.department) ? ' (แผนกปัจจุบัน)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="รายละเอียด (ไม่บังคับ)"
            placeholder="เช่น กำลังดำเนินการโดยช่างประปา"
            value={statusConfirmDescription}
            onChange={(e) => setStatusConfirmDescription(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': { borderColor: 'rgba(132, 94, 194, 0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(132, 94, 194, 0.5)' },
                '&.Mui-focused fieldset': { borderColor: '#845EC2' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleStatusConfirmClose} sx={{ borderRadius: 2 }}>
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            onClick={handleStatusConfirmSubmit}
            disabled={
              statusUpdatingId === statusConfirmItem?.id ||
              (statusConfirmNewStatus === 'forward' && !statusConfirmDepartment)
            }
            sx={{ borderRadius: 2, bgcolor: '#845EC2', '&:hover': { bgcolor: '#6344A4' } }}
          >
            {statusUpdatingId === statusConfirmItem?.id ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'ยืนยัน'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={updateSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setUpdateSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setUpdateSnackbar((p) => ({ ...p, open: false }))}
          severity={updateSnackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {updateSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServiceProviderDashboard;
